/**
 * useScopeBarPrefetch — préchauffe le cache React-Query pour les 4 référentiels
 * consommés par la ScopeBar du MX RM Flow (regions, wilayas, brands, price-segments).
 *
 * Pourquoi : au mount de `/marche`, ~22-24 fetches partent en parallèle. La limite
 * HTTP/1.1 du navigateur (~6 connexions par origine) empile les requêtes ; les 4
 * référentiels ScopeBar finissaient piégés derrière le peloton (KPI cards + 10
 * charts B + wholesale) → axios timeout > 30s → CANCELED → dropdowns vides.
 *
 * Fix : on déclenche ces 4 prefetch dès l'AppShell (post-auth, avant tout page-mount).
 * staleTime 5 min côté QueryClient global → les hooks de la ScopeBar liront le cache
 * sans déclencher un nouveau réseau.
 *
 * Les queryKey + queryFn utilisés ICI DOIVENT être identiques à ceux des hooks :
 *   - useCascadeFilter('regions' | 'wilayas' | 'brands') — voir useCascadeFilter.ts
 *   - usePriceSegmentsRef — voir usePriceSegmentsRef.ts
 *
 * On réutilise donc directement la queryFn exportée par usePriceSegmentsRef
 * (fetchPriceSegmentsRef + PRICE_SEGMENTS_REF_KEY) pour zéro drift.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fetchLabDimensions } from '@/modules/self-service-lab/lib/api';
import type { CascadeOption } from '@/modules/self-service-lab/hooks/useCascadeFilter';
import {
  fetchPriceSegmentsRef,
  PRICE_SEGMENTS_REF_KEY,
} from './usePriceSegmentsRef';

/**
 * Construit la queryKey identique à celle de useCascadeFilter(dim, {}).
 * stableKey = [dimensionType, brand_id ?? '', region_ids.join(','), wilaya_ids.join(','),
 *              merch_ids.join(','), search ?? '']
 * → 8 éléments avec le préfixe ['lab', 'dimensions', ...stableKey].
 */
function cascadeKey(dimensionType: string) {
  return ['lab', 'dimensions', dimensionType, '', '', '', '', ''] as const;
}

export function useScopeBarPrefetch() {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Fire-and-forget : on ne bloque pas le render.
    // Chaque prefetch est idempotent (queryClient ignore si cache déjà frais).
    void queryClient.prefetchQuery({
      queryKey: cascadeKey('regions'),
      queryFn:  () => fetchLabDimensions('regions', {}) as Promise<CascadeOption[]>,
      staleTime: 5 * 60_000,
    });
    void queryClient.prefetchQuery({
      queryKey: cascadeKey('wilayas'),
      queryFn:  () => fetchLabDimensions('wilayas', {}) as Promise<CascadeOption[]>,
      staleTime: 5 * 60_000,
    });
    void queryClient.prefetchQuery({
      queryKey: cascadeKey('brands'),
      queryFn:  () => fetchLabDimensions('brands', {}) as Promise<CascadeOption[]>,
      staleTime: 5 * 60_000,
    });
    void queryClient.prefetchQuery({
      queryKey: PRICE_SEGMENTS_REF_KEY,
      queryFn:  fetchPriceSegmentsRef,
      staleTime: 5 * 60_000,
    });
  }, [queryClient]);
}
